import {
  DraftConjointItem,
  DraftTextHighlighterItem
} from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftMatrixItem,
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  DraftTextCardItem,
  EntryType
} from '../../../data/model/questionnaire'
import { getQuestionEntryPrefix } from '../../../utils/questionnaireUtils'
import { isHiddenIn } from './marketVisibility'

// Forks and sections are structural, not respondent questions, so they never
// appear as mapping rows (sections are already expanded by flattenEntries).
const NON_QUESTION_ENTRY_TYPES: ReadonlyArray<EntryType> = [
  EntryType.ForkEntryType,
  EntryType.SectionEntryType
]

export type MappingCell =
  | { excluded: true }
  // `full` = the question is present in the market with every option/row (and,
  // for a matrix, no hidden columns). visible < total — or a hidden matrix
  // column — makes it partial even though it isn't excluded.
  | { excluded: false; visible: number; total: number; full: boolean }

export interface MappingRow {
  id: string
  identifier: string
  text: string
  // True when the question and all its options are present in every market —
  // then the whole row reads as "Common". Otherwise each cell shows its own
  // per-market status (a count, or excluded).
  isCommon: boolean
  cells: MappingCell[]
}

const getEntryText = (entry: DraftQuestionnaireEntry): string => {
  switch (entry.entryType) {
    case EntryType.QuestionEntryType:
      return (entry.entryItem as DraftQuestionItem).question?.text ?? ''
    case EntryType.MatrixEntryType:
      return (entry.entryItem as DraftMatrixItem).matrixTitle?.title ?? ''
    case EntryType.TextCardEntryType:
      return (entry.entryItem as DraftTextCardItem).textCard.title
    case EntryType.TextHighlighterEntryType:
      return (entry.entryItem as DraftTextHighlighterItem).question?.text ?? ''
    case EntryType.ConjointEntryType:
      return (entry.entryItem as DraftConjointItem).question?.text ?? ''
    default:
      return ''
  }
}

/**
 * The hideable "options" that drive a cell's count: response options for a
 * question, rows for a matrix (the per-row visibility researchers set). Matrix
 * response options (columns) can also be hidden — those don't change the count
 * but do make a market non-full, so they're reported separately.
 */
const getOptionInfo = (
  entry: DraftQuestionnaireEntry,
  marketSurveyId: string
): { total: number; visible: number; full: boolean } => {
  let options: { hiddenInMarkets?: string[] | null }[] = []
  let columns: { hiddenInMarkets?: string[] | null }[] = []

  if (entry.entryType === EntryType.QuestionEntryType) {
    options = (entry.entryItem as DraftQuestionItem).responseOptions
  } else if (entry.entryType === EntryType.MatrixEntryType) {
    const item = entry.entryItem as DraftMatrixItem
    options = item.matrixRows
    columns = item.responseOptions
  }

  const total = options.length
  const visible = options.filter(
    (option) => !isHiddenIn(option.hiddenInMarkets, marketSurveyId)
  ).length
  const columnsHidden = columns.some((column) =>
    isHiddenIn(column.hiddenInMarkets, marketSurveyId)
  )

  return { total, visible, full: visible === total && !columnsHidden }
}

/**
 * Builds the Question × Market mapping grid from the (flattened) master
 * questionnaire and the project's market survey ids. For each question, each
 * market cell is either excluded (the question is hidden there) or present with
 * a visible-option count. A row is "Common" when the question and all its
 * options are present in every market.
 */
export const computeMarketMapping = (
  entries: DraftQuestionnaireEntry[],
  marketSurveyIds: string[]
): MappingRow[] =>
  entries
    .filter((entry) => !NON_QUESTION_ENTRY_TYPES.includes(entry.entryType))
    .map((entry) => {
      let isCommon = marketSurveyIds.length > 0
      const cells = marketSurveyIds.map((marketSurveyId): MappingCell => {
        if (isHiddenIn(entry.hiddenInMarkets, marketSurveyId)) {
          isCommon = false
          return { excluded: true }
        }
        const { total, visible, full } = getOptionInfo(entry, marketSurveyId)
        if (!full) isCommon = false
        return { excluded: false, visible, total, full }
      })

      return {
        id: entry.id,
        // Per-type label (T1/Q1/S1/A1 + contextPosition) — the same identifier
        // the survey editor and docx export show, so a leading text
        // instruction reads "T1" here, not "Q1".
        identifier: getQuestionEntryPrefix(entry),
        text: getEntryText(entry),
        isCommon,
        cells
      }
    })
